import { useEffect, useState } from "react";
import { createDebounce } from "../../../../../common/helpers/debounce.helper";
import {
  getVehicles,
  getVehiclesByPartnerId,
} from "../../../../../common/services/vehicles.api";
import { IVehicle, VEHICLE_STATUS } from "../../../../../types/vehicle.type";
import AutocompleteFormik from "../../../../../common/components/form/AutocompleteFormik";
import { useFormikContext } from "formik";
// import { filterVehiclesByPlan } from "../../../../../common/helpers/filter-vehicles-plan";
import { formatCompanyName } from "@/common/helpers/format-company-name";
// import { getVehiclesPaged } from "../../../../../common/services/vehicles.api";

const getVehiclesByPartnerDebounced = createDebounce(getVehiclesByPartnerId, 100);
const getVehiclesDebounced = createDebounce(getVehicles, 100);

type Props = {
  required?: boolean;
  status?: VEHICLE_STATUS;
  notRented?: boolean;
  partnerId?: string;
  companyId: string;
  renterId?: string;
  plan?: string;
  listAll?: boolean;
  idCompanyB2B?: string | null;
  allowedPlates?: string[] | null;
  reservedVehicle?: boolean;
};

export function AvailableVehiclesFieldFormik({
  required = false,
  status = VEHICLE_STATUS.AVAILABLE,
  plan = "",
  partnerId,
  renterId,
  companyId,
  listAll = true,
  allowedPlates,
  reservedVehicle
}: Props) {
  const { setFieldValue } = useFormikContext();
  const [vehicles, setVehicles] = useState<
    (IVehicle & { label: string; _id: string })[]
  >([]);
  const [loading, setLoading] = useState(false);

  const loadOptions = async (input: string) => {
    setLoading(true);
    setVehicles([]);

    const fieldsToReceive =
      "_id,companyId,brand,model,licensePlate,odometerLocavibe,status,partnerInfo.companyName, createdAt, daysAtBase";
    const shouldLoadGlobalLocaVibeVehicles =
      listAll && !partnerId && !renterId && !companyId;

    const result = shouldLoadGlobalLocaVibeVehicles
      ? ((await getVehiclesDebounced({
          itemsPerPage: "1000",
          search: input,
          fields: fieldsToReceive,
          status,
        })) as { ok: boolean; data: IVehicle[] })
      : ((await getVehiclesByPartnerDebounced({
          itemsPerPage: 1000,
          search: input,
          fields: fieldsToReceive,
          partnerId: partnerId ? partnerId : undefined,
          renterId: renterId ? renterId : undefined,
          companyId: companyId,
          listAll: listAll,
          status: !listAll ? VEHICLE_STATUS.AVAILABLE : status,
          exchangeForRenterId: !listAll && renterId ? renterId : undefined,
          plan: plan || undefined,
          reservedVehicle: reservedVehicle,
        })) as { ok: boolean; data: IVehicle[] });

    if (!result.ok) {
      setLoading(false);
      return [];
    }

    // Filtra veículos com base no plano selecionado
    let filteredVehicles = result.data;

    if (!listAll) {
      filteredVehicles = filteredVehicles.filter(
        (vehicle) => vehicle.status === VEHICLE_STATUS.AVAILABLE
      );
    }


    // 🆕 Filtra por placas permitidas se houver allowedPlates
    if (allowedPlates && allowedPlates.length > 0) {
      filteredVehicles = filteredVehicles.filter((vehicle) =>
        allowedPlates.includes(vehicle.licensePlate)
      );
    }

    const options = filteredVehicles.map((item) => ({
      ...item,
      label: `${item.licensePlate} | ${formatCompanyName(
        item.partnerInfo?.companyName
      )}- ODO: ${item.odometerLocavibe} km | `,
      daysAtBase: item.daysAtBase || 0,
      _id: item._id,
    }));

    setVehicles(options);
    setLoading(false);
    return options;
  };

  useEffect(() => {
    const fetchOptions = async () => {
      await loadOptions("");
      setFieldValue("vehicle", null);
    };
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, allowedPlates, reservedVehicle]);

  // 🆕 Mensagem quando não há placas permitidas
  const isB2BMode = allowedPlates !== undefined && allowedPlates !== null;
  const hasNoAllowedPlates = isB2BMode && allowedPlates.length === 0;

  return (
    <div>
      {hasNoAllowedPlates && (
        <small className="text-redPrimary mb-2 block">
          Nenhuma placa permitida para este B2B
        </small>
      )}
      <AutocompleteFormik
        name="vehicle"
        label={
          isB2BMode
            ? `Veículo B2B (${allowedPlates.length} placas permitidas) ${loading ? "(Carregando...)" : ""
            }`
            : "Veículo"
        }
        options={vehicles}
        required={required}
        disableInvalidVehicles={true}
        exactMatchOnly={true}
      />
    </div>
  );
}
